import { Component, EventEmitter, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TableViewInterfaceComponent } from './tableViewInterfaceComponent';
import { SearchFilters } from '../../../models/search/searchFilters';
import { Options } from './options';

export class Page<T> {
  public records: T[];
  public totalCount: number;
  public pageNum: number;
  public pageSize: number;

  constructor(x: Partial<Page<T>>) {
    // TODO (jacob): Should this check happen on the backend?
    if (x?.records == undefined) x.records = [];

    this.records = x.records;
    this.totalCount = x.totalCount ?? this.records.length;
    this.pageNum = x.pageNum ?? 0;
    this.pageSize = x.pageSize ?? 10;
  }
}

@Component({
  template: '',
  standalone: false,
})
export abstract class TableViewAbstractComponent
  implements TableViewInterfaceComponent
{
  @Output() selectedRecord = new EventEmitter<any>();
  @Output() recordSetUpdating = new EventEmitter<boolean>();
  @Output() recordSetUpdated = new EventEmitter<any>();
  @Output() genericOutputEvent = new EventEmitter<any>();

  public options: Options = new Options({});
  public selectRoute: string = '';
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  selectAction: string = ''; //none|details (expand)|[empty] (emit selected id)
  filterTimeout: boolean = false;
  queryInProgress: boolean = false;

  _intervalId: number | undefined;
  _lastQuery: string = '';
  _pageSize: number = 0;
  _recordCount: number = 0;
  _sortColumn: string = '';
  _sortDirection: string = 'asc';

  cachedReloadRequest: {
    query: string;
    filters: SearchFilters;
    pageNum: number;
    pageSize: number;
  } | undefined


  async reloadCachedRequest(): Promise<boolean> {
    // console.log('reloadCachedRequest', this.cachedReloadRequest);
    if (this.cachedReloadRequest) {
      await this._reload(
        this.cachedReloadRequest.query,
        new SearchFilters({}),
        this.cachedReloadRequest.pageNum,
        this.cachedReloadRequest.pageSize,
        this._sortColumn,
        this._sortDirection,
      );
      return true;
    } else return false;
  }

  constructor() {}
  
  abstract getPageOfData(
    query: string,
    filters: any,
    pageNum: number,
    pageSize: number,
    sortColumn: string,
    sortDirection: string,
    options: Options,
  ): Promise<Page<any>>;
  abstract getRecordFromDataSource(recordId: string): any;
  abstract canQuery(): boolean;

  // TODO: Consider changing the name of this to something like load(),
  // since this function has to get called in order for the data to load.
  public setOptions(options: Options) {
    if (options != null) this.options = options;

    if (!this.options.pageSize) {
      this._pageSize = 10;
    } else {
      this._pageSize = this.options.pageSize;
    }

    // We know pageSize is set by this line
    if (this.options.queryOnLoad != false) {
      this.reload(
        '',
        new SearchFilters({}),
        0,
        this.options.pageSize as number,
      );
    }

    if (this.options.pollms != null && this.options.pollms > 0) {
      const that = this;
      this._intervalId = window.setInterval(() => {
        return that.reload(
          '',
          new SearchFilters({}),
          0,
          that.options.pageSize as number,
        );
      }, this.options.pollms);
    }
  }

  clearSelection(): void {}

  async reload(
    query: string,
    filters: SearchFilters,
    pageNum: number,
    pageSize: number,
  ): Promise<boolean> {

    this.cachedReloadRequest = { query, filters, pageNum, pageSize }
    return this._reload(
      query,
      filters,
      pageNum,
      pageSize,
      this._sortColumn,
      this._sortDirection,
    );
  }

  async _reload(
    query: string,
    filters: SearchFilters,
    pageNum: number,
    pageSize: number,
    sortColumn: string,
    sortDirection: string,
  ): Promise<boolean> {
    if (
      !this.queryInProgress &&
      this.canQuery() /*&& (this.options.merchantId != undefined || this.options.customerId != undefined)*/
    ) {
      this.recordSetUpdating.emit(true);

      this._lastQuery = query;
      this._sortColumn = sortColumn;
      this._sortDirection = sortDirection;

      this._pageSize =
        pageSize != undefined && pageSize > 0 ? pageSize : this._pageSize;

      this.queryInProgress = true;
      let that = this;

      let pageOfResults = await this.getPageOfData(
        query,
        filters,
        pageNum,
        this._pageSize,
        sortColumn,
        sortDirection,
        this.options,
      );

      this.queryInProgress = false;

      if (pageOfResults) {
        this.dataSource.data = pageOfResults.records;
        this._recordCount = pageOfResults.totalCount
          ? pageOfResults.totalCount
          : 0;

        let firstId: number | undefined = undefined;

        if (pageOfResults.records.length > 0) {
          firstId = pageOfResults.records[0].id;
        }

        this.recordSetUpdated.emit({
          recordCount: this._recordCount,
          firstId: firstId,
        });
      }

      this.recordSetUpdating.emit(false);

      return new Promise<boolean>((resolve) => {
        resolve(true);
      });
    }

    return new Promise<boolean>((resolve) => {
      resolve(false);
    });
  }

  public sortBy_click(columnName: string) {
    let direction = this._sortDirection;
    if (columnName == this._sortColumn) {
      // same column clicked, so switch direction
      if (this._sortDirection == 'asc') {
        direction = 'desc';
      } else {
        direction = 'asc';
      }
    }

    this._reload(
      this._lastQuery,
      new SearchFilters({}),
      0,
      this._pageSize,
      columnName,
      direction,
    );
  }

  selectRow_click(recordId: string) {
    let selectedRecord = this.getRecordFromDataSource(recordId);

    if (this.options.selectAction == 'details') {
      // deselect selected summary row
      let alltrsummary = document.getElementsByClassName(
        'selected',
      ) as HTMLCollectionOf<HTMLElement>;
      for (let i = 0; i < alltrsummary.length; i++) {
        alltrsummary[i].classList.remove('selected');
      }

      // deselect details row
      let alltrdetails = document.getElementsByClassName(
        'rowDetails',
      ) as HTMLCollectionOf<HTMLElement>;
      for (let i = 0; i < alltrdetails.length; i++) {
        alltrdetails[i].style.display = 'none';
      }

      // select the current row
      let trSummary = document.getElementById('trsummary_' + recordId);
      let trDetails = document.getElementById('trdetails_' + recordId);

      if (trDetails != null) {
        trDetails.style.display = 'table-row';
      }
      trSummary?.classList.add('selected');
    } else {
      this.selectedRecord.emit(selectedRecord);
    }
  }
}
